import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { colors } from '../constants';

import LineSpinner from './LineSpinner';

interface StyledOptionButtonProps {
  selected: boolean;
}

interface StyledSpinnerButtonProps {
  submitting?: boolean;
}

interface SpinnerButtonProps {
  id: string;
  type: 'button' | 'submit' | 'reset';
  disabled: boolean;
  submitting: boolean;
  buttonText: string;
}

interface TextButtonProps {
  disabled?: boolean;
}

// The primary button styles, for use in any styled-component...
// ...that wants to look like the PrimaryButton
const ButtonStyles = css`
  height: 3.5rem;
  border-radius: 2rem;
  border: 0.0625rem solid transparent;
  font-size: 1.16875rem;
  font-weight: 700;
  text-align: center;
  color: ${colors.TEXT_PRIMARY};
  background-color: transparent;
  outline: none;
  appearance: none;
  cursor: pointer;
`;

// The primary button styles, for use in any styled-component...
// ...that wants to look like the PrimaryButton
const PrimaryButtonStyles = css`
  ${ButtonStyles}
  border-color: ${colors.UI_PRIMARY};
  color: #ffffff;
  background-color: ${colors.UI_PRIMARY};
  transition: all 0.1s ease;

  /* Hover/Focus - darken background color and add box shadow */
  &:hover,
  &:focus {
    box-shadow: 0 0 0.25rem ${colors.UI_PRIMARY_ACCENT}26;
    border-color: ${colors.UI_PRIMARY_ACTION};
    background-color: ${colors.UI_PRIMARY_ACTION};
  }

  /* Undo on-focus UI if focus-visible property is supported */
  &:focus:not(:hover, :focus-visible) {
    box-shadow: none;
    border-color: ${colors.UI_PRIMARY};
    background-color: ${colors.UI_PRIMARY};
  }

  /* Apply on-focus UI only when keyboard-focused */
  &:focus-visible {
    box-shadow: 0 0 0.25rem ${colors.UI_PRIMARY_ACCENT}26;
    border-color: ${colors.UI_PRIMARY_ACTION};
    background-color: ${colors.UI_PRIMARY_ACTION};
  }

  /* Disabled - change background color to light gray and disable hover feedback */
  &:disabled {
    box-shadow: none;
    border-color: transparent;
    color: #ffffff;
    background-color: #d8d8d8;
    cursor: no-drop;
  }
`;

export const Button = styled.button`
  ${ButtonStyles}
`;
Button.displayName = 'Button';

// The base button, but as a react-router link
export const ButtonLink = styled((props: any) => <Link {...props} />)`
  ${ButtonStyles}
`;
ButtonLink.displayName = 'ButtonLink';

// Base button with orange background color
export const PrimaryButton = styled(Button)`
  ${PrimaryButtonStyles}
`;
PrimaryButton.displayName = 'PrimaryButton';

// Primary button with responsive width styling for CTA buttons at the bottom of funnel pages
export const ActionButton = styled(PrimaryButton)<StyledSpinnerButtonProps>`
  width: 100%;
  max-width: 25rem;
  &:disabled {
    box-shadow: none;
    border-color: transparent;
    color: #ffffff;
    background-color: ${(p: StyledSpinnerButtonProps) =>
      p.submitting ? colors.UI_PRIMARY : colors.BORDER_SECONDARY};
    cursor: no-drop;
  }
`;
ActionButton.displayName = 'ActionButton';

// Anchor element styled like a CTA button
export const ActionAnchor = styled.a`
  ${PrimaryButtonStyles}
  width: 100%;
  max-width: 25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
`;
ActionAnchor.displayName = 'ActionAnchor';

// The CTA button, but as a react-router link
export const ActionButtonLink = styled((props: any) => <Link {...props} />)`
  ${PrimaryButtonStyles}
  width: 100%;
  max-width: 25rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-decoration: none;
`;
ActionButtonLink.displayName = 'ActionButtonLink';

// Complementary to PrimaryButton, light-gray with gray border color
export const OptionButton = styled(Button)<StyledOptionButtonProps>`
  height: 3rem;
  border-color: ${colors.BORDER_SECONDARY};
  font-size: 1rem;
  color: ${colors.TEXT_PRIMARY};
  background-color: ${colors.UI_SECONDARY};
  transition: all 0.1s ease;

  /* Hover/Focus - add orange background, border, and font color */
  &:hover,
  &:focus {
    box-shadow: none;
    border-color: ${colors.TEXT_ACTION};
    color: ${colors.TEXT_ACTION};
    background-color: ${colors.UI_SECONDARY};
    ${(p: StyledOptionButtonProps) =>
      p.selected &&
      `
      background-color: ${colors.UI_SECONDARY_ACTION};
    `};
  }

  /* Undo on-focus UI if focus-visible property is supported */
  &:focus:not(:hover) {
    border-color: ${colors.BORDER_SECONDARY};
    color: ${colors.TEXT_PRIMARY};
    background-color: ${colors.UI_SECONDARY};
    ${(p: StyledOptionButtonProps) =>
      p.selected &&
      `
      border-color: ${colors.TEXT_ACTION};
      color: ${colors.TEXT_ACTION};
      background-color: ${colors.UI_SECONDARY_ACTION};
    `};
  }

  /* Apply on-focus UI only when keyboard-focused - Follows Prosper's new Shape Design System */
  &:focus-visible {
    transition: z-index 0.5s;
    z-index: 1;
    outline: ${colors.UI_PRIMARY} solid 0.125rem;
    outline-offset: 0.0625rem;
    ${(p: StyledOptionButtonProps) =>
      p.selected &&
      `
      background-color: ${colors.UI_SECONDARY_ACTION};
    `};
  }

  ${(p: StyledOptionButtonProps) =>
    p.selected &&
    `
    border-color: ${colors.TEXT_ACTION};
    color: ${colors.TEXT_ACTION};
    background-color: ${colors.UI_SECONDARY_ACTION};
  `};

  /* Disabled - change background color to light gray and disable hover feedback */
  &:disabled {
    box-shadow: none;
    border-color: transparent;
    color: ${colors.TEXT_LABEL};
    background-color: ${colors.UI_SECONDARY};
    cursor: no-drop;
    ${(p: StyledOptionButtonProps) =>
      p.selected &&
      `
      border-color: ${colors.TEXT_LABEL};
    `};
  }
`;
OptionButton.displayName = 'OptionButton';

// Secondary button used for activating/displaying additional information relevant to current page
export const SupplementButton = styled(Button)`
  border-radius: 3.5rem;
  border: 0.0625rem solid ${colors.UI_PRIMARY};
  box-sizing: border-box;
  color: ${colors.TEXT_ACTION};
  font-size: 0.875rem;
  font-weight: 400;
  height: 2.5rem;
  transition: all 0.1s ease;
  width: 8.625rem;

  &:hover,
  &:focus {
    border-color: ${colors.TEXT_ACTION};
    background-color: ${colors.UI_SECONDARY_ACTION};
  }

  &:focus:not(:hover, :focus-visible) {
    border-color: ${colors.UI_PRIMARY};
    background-color: transparent;
  }
`;
SupplementButton.displayName = 'SupplementButton';

export const InlineButton = styled.button`
  text-decoration: underline;
  font-weight: 400;
  text-align: center;
  line-height: 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  &:hover,
  &:focus {
    text-decoration: underline;
  }
  ${(p: TextButtonProps) =>
    p.disabled &&
    `
    &&& {
      color: ${colors.TEXT_LABEL};
      text-decoration: none;
      cursor: not-allowed;
      &:hover, &:focus {
      text-decoration: none;
    }
  };
  `};
`;
InlineButton.displayName = 'InlineButton';

const SpinnerButtonSpinner = styled(LineSpinner)`
  width: 1.5rem;
  height: 1.5rem;
`;
SpinnerButtonSpinner.displayName = 'SpinnerButtonSpinner';

// A button which can replace its text with a loading spinner when submitting
export const SpinnerButton = (props: SpinnerButtonProps) => {
  const { id, type, disabled, submitting, buttonText } = props;
  return (
    <ActionButton
      id={id}
      type={type}
      disabled={disabled}
      submitting={submitting}
    >
      {submitting ? <SpinnerButtonSpinner /> : buttonText}
    </ActionButton>
  );
};
